import { graphql } from "gatsby";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";

import { dateIsPast } from "~/utilities/formatDates";
import { getLocalStorageItem } from "~/utilities/localStorageHelpers";
import { screenReaderText } from "~/styles/helpers";

import { useFormContext } from "~/context/FormContext";

import ContentContainer from "~/components/layout/ContentContainer";
import Primary from "~/components/layout/Primary";
import TwoColumnLayout from "~/components/layout/TwoColumnLayout";
import ErrorBoundary from "~/components/ErrorBoundary";
import Seo from "~/components/Seo";
import ArticleMetaData from "~/components/ArticleMetaData";

import { popHeroBlock, shiftHeroBlock } from "~/utilities/helpers";
import { formatArticleDate } from "~/utilities/formatDates";

import {
    ArticleBlockList,
    ArticleBreadcrumbs,
    ArticleCtaBar,
    ArticleGatedArticle,
    ArticleKnowledgeCenter,
    ArticlePageHeader,
    ArticleSidebar,
} from "./components";

const ComingSoonText = styled.h2`
    font-size: 5rem;
    font-weight: bold;
`;

const SrOnlyH3 = styled.h3`
    ${screenReaderText}
`;

const EmptyWrapper = styled.div``;

const Header = ({
    hidePageHeader,
    location,
    data,
    urlPermutations,
    breadcrumbsAtTop,
}) => {
    if (hidePageHeader) {
        const wpArticle = data?.wpArticle || {};
        const newWpArticle = popHeroBlock(wpArticle);

        if (isEmpty(newWpArticle)) {
            return null;
        }

        const newData = { ...data, wpArticle: newWpArticle };

        return (
            <ArticleBlockList
                data={newData}
                urlPermutations={urlPermutations}
            />
        );
    }

    return (
        <ErrorBoundary>
            {breadcrumbsAtTop ? (
                <>
                    <ArticleBreadcrumbs data={data} />
                    <ArticlePageHeader
                        data={data}
                        location={location}
                        urlPermutations={urlPermutations}
                    />
                </>
            ) : (
                <>
                    <ArticlePageHeader
                        data={data}
                        location={location}
                        urlPermutations={urlPermutations}
                    />
                    <ArticleBreadcrumbs data={data} />
                </>
            )}
        </ErrorBoundary>
    );
};

const MainContent = ({
    translationGroupId,
    details,
    location,
    isComingSoonArticle,
    data,
    urlPermutations,
}) => {
    const [loading, setLoading] = useState(!!details.gated);

    useEffect(() => {
        if (loading) {
            setLoading(false);
        }
    }, [loading]);

    const gatedFormCompleted = getLocalStorageItem(`kc-${translationGroupId}`);

    const isGated =
        details.gated &&
        gatedFormCompleted !== "completed" &&
        !dateIsPast(details.gatedUntil);

    if (loading) {
        return <h2>Please Wait....</h2>;
    }

    if (isComingSoonArticle) {
        return <ComingSoonText>Coming Soon!</ComingSoonText>;
    }

    if (isGated) {
        return <ArticleGatedArticle data={data} location={location} />;
    }

    const wpArticle = data?.wpArticle || {};
    const newWpArticle = shiftHeroBlock(wpArticle);
    const newData = { ...data, wpArticle: newWpArticle };

    return (
        <ArticleBlockList data={newData} urlPermutations={urlPermutations} />
    );
};

const Article = ({ data, location, pageContext }) => {
    const {
        wpArticle: {
            articleMetadata,
            languageCode,
            articleOptions,
            isComingSoonArticle,
            title,
            articleTopics,
            details,
            translationGroupId,
            date,
            primaryTopic: { primaryTopic },
            articleOptions: { breadcrumbsAtTop },
        },
    } = data;

    const { setHiddenFields } = useFormContext();

    useEffect(() => {
        setHiddenFields({
            pageTitle: title,
            pageUrl: location.href,
        });
    }, [
        setHiddenFields,
        location.href,
        articleTopics.nodes,
        primaryTopic,
        title,
    ]);

    const { hidePageHeader, hideSidebar, isFullWidth } = articleOptions || {};

    const urlPermutations = pageContext.urlPermutations;

    const Wrapper = isFullWidth ? EmptyWrapper : ContentContainer;

    const articleDate =
        isComingSoonArticle && details.releaseDate
            ? formatArticleDate(details.releaseDate)
            : date;

    return (
        <Primary {...{ urlPermutations, pageCode: languageCode }}>
            <Header
                hidePageHeader={hidePageHeader}
                location={location}
                data={data}
                urlPermutations={urlPermutations}
                breadcrumbsAtTop={breadcrumbsAtTop}
            />

            <ArticleMetaData
                date={articleDate}
                title={title}
                fullWidth={hideSidebar}
                articleMetadata={articleMetadata}
            />

            <Wrapper>
                <SrOnlyH3 tabIndex="0">Article</SrOnlyH3>
                <TwoColumnLayout
                    stickySidebar
                    main={
                        <MainContent
                            {...{
                                translationGroupId,
                                details,
                                location,
                                isComingSoonArticle,
                                data,
                                urlPermutations,
                            }}
                        />
                    }
                    sidebar={<ArticleSidebar data={data} />}
                    hideSidebar={hideSidebar}
                    hideSidebarOnMobile
                />
            </Wrapper>

            <ArticleCtaBar data={data} />

            <ArticleKnowledgeCenter data={data} />
        </Primary>
    );
};

export default Article;

export const Head = ({ data, location, pageContext }) => {
    const {
        wpArticle: { title, featuredImage, details, seo, language },
    } = data;

    const urlPermutations = pageContext.urlPermutations;

    return (
        <Seo
            {...{ seo, featuredImage, location, urlPermutations }}
            pageTitle={title}
            cardCopy={details.cardCopy}
            languageCode={language?.code}
        />
    );
};

export const query = graphql`
    query ArticleQuery(
        $id: String!
        $primaryTopicSlug: String
        $languageCode: String
    ) {
        wp {
            optionsPage {
                globalOptions {
                    hideTwitter {
                        hideKcTwitter
                    }
                }
            }
        }
        sameLanguageArticles: allWpArticle(
            filter: {
                articleTopics: {
                    nodes: { elemMatch: { slug: { eq: $primaryTopicSlug } } }
                }
                languageOptions: {
                    excludeFromLanguage: { nin: [$languageCode] }
                }
                languageCode: { in: [$languageCode] }
                id: { ne: $id }
            }
            sort: { date: DESC }
            limit: 3
        ) {
            nodes {
                ...ArticleFragment
            }
        }
        allWpArticle(
            filter: {
                articleTopics: {
                    nodes: { elemMatch: { slug: { eq: $primaryTopicSlug } } }
                }
                languageOptions: {
                    excludeFromLanguage: { nin: [$languageCode] }
                }
                languageCode: { in: ["en", null] }
                id: { ne: $id }
            }
            sort: { date: DESC }
            limit: 3
        ) {
            nodes {
                ...ArticleFragment
            }
        }
        wpArticle(id: { eq: $id }) {
            slug
            id
            title
            link
            isComingSoonArticle
            date(formatString: "MMM DD, YYYY")
            articleMetadata {
                ...ArticleMetadataFragment
            }
            seo {
                ...SeoPostFragment
            }
            articleOptions {
                hideSidebar
                hidePageHeader
                breadcrumbsAtTop
                isFullWidth
            }
            featuredImage {
                node {
                    sourceUrl
                }
            }
            details {
                cardCopy
                gatedPreviewCopy
                gated
                gatedUntil
                overrideFormId
                iconClassOverride
                relatedKnowledgeCenterArticles {
                    ... on WpArticle {
                        ...ArticleFragment
                    }
                }
                releaseDate
            }
            ctaSections {
                sections {
                    title
                    links {
                        linkLabel
                        linkUrl
                        icon
                        type
                        formId
                        doc {
                            ...DocumentFragment
                        }
                    }
                }
            }
            articleTopics {
                nodes {
                    link: uri
                    name
                    slug
                    taxonomyDetails {
                        translatedName {
                            languageCode
                            translation
                        }
                    }
                }
            }
            primaryTopic {
                primaryTopic {
                    slug
                    name
                    link: uri
                }
            }
            productCategories {
                nodes {
                    slug
                    ancestors {
                        nodes {
                            slug
                        }
                    }
                }
            }
            productLines {
                nodes {
                    link: uri
                    name
                    slug
                }
            }
            languageCode
            language {
                code
            }
            translationGroupId
            translations {
                id
                title
                slug
                languageCode
                languageName
            }
            blocks {
                ...AcfQuoteBlockFragment
                ...AccordionsBlockFragment
                ...AdFullBlockFragment
                ...AdWithDropdownBlockFragment
                ...AlertBlockFragment
                ...ButtonsBlockFragment
                ...CardLinksBlockFragment
                ...CodeBlockFragment
                ...ColumnsBlockFragment
                ...ContactBlockFragment
                ...ContentBlockFragment
                ...CookieTypesBlockFragment
                ...CtaSectionsBlockFragment
                ...DocumentBlockFragment
                ...DocumentLibraryBlockFragment
                ...EmbedBlockFragment
                ...EventsBlockFragment
                ...FeaturedKcLeadershipBlockFragment
                ...FeaturedNewsBlockFragment
                ...FeaturedProductsCarouselBlockFragment
                ...FeaturedResourcesBlockFragment
                ...FileBlockFragment
                ...FormBlockFragment
                ...FreeformBlockFragment
                ...FullWidthCtaBlockFragment
                ...GraphicWithHotspotsBlockFragment
                ...GroupBlockFragment
                ...GroupOfLinksBlockFragment
                ...HeadingBlockFragment
                ...HeroBlockFragment
                ...HeroSliderBlockFragment
                ...HtmlBlockFragment
                ...ImageBlockFragment
                ...ImageSliderBlockFragment
                ...InfoCardsBlockFragment
                ...IntroductionBlockFragment
                ...JsEmbedBlockFragment
                ...JumpLinksBlockFragment
                ...KnowledgeCenterBlockFragment
                ...KnowledgeCenterFilterBlockFragment
                ...LatestNewsBlockFragment
                ...LightboxImageBlockFragment
                ...LinkSectionBlockFragment
                ...ListBlockFragment
                ...LottieBlockFragment
                ...MathjaxBlockFragment
                ...MaterialsPartsBuilderBlockFragment
                ...MediaTextBlockFragment
                ...NewsBlockFragment
                ...NewsCardCarouselBlockFragment
                ...PageHeaderBlockFragment
                ...PartnersBlockFragment
                ...ParagraphBlockFragment
                ...PastEventsBlockFragment
                ...ProductTableBlockFragment
                ...ProductCardSectionBlockFragment
                ...PullquoteBlockFragment
                ...QuoteBlockFragment
                ...ReferenceDesignCardsBlockFragment
                ...ReferenceDesignsTableBlockFragment
                ...ResourcesBlockFragment
                ...SeparatorBlockFragment
                ...SidebarTabsBlockFragment
                ...SidebarTitleSectionBlockFragment
                ...SpacerBlockFragment
                ...StatCardsBlockFragment
                ...TabbedAdBlockFragment
                ...TableBlockFragment
                ...TabSectionBlockFragment
                ...TestimonialsBlockFragment
                ...ThreeColumnCardsBlockFragment
                ...TimelineBlockFragment
                ...TwitterTimelineBlockFragment
                ...UpcomingEventsBlockFragment
                ...VideoBlockFragment
                ...VideoAdBlockFragment
                ...VideoEmbedBlockFragment
                ...VideoPlaylistBlockFragment
                ...WhereToBuyBlockFragment
            }
        }
    }
`;
